/**
 * GlobalLoadingIndicator Component
 *
 * Issue #1284: Unified loading state management
 *
 * Displays a global loading indicator when any TanStack Query
 * operation is in progress (fetching or mutating).
 *
 * @example
 * ```tsx
 * // In App.tsx or layout component
 * <GlobalLoadingIndicator />
 * ```
 */

import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { cn } from '@/lib/utils';

interface GlobalLoadingIndicatorProps {
  /** Additional CSS classes */
  className?: string;
  /** Show indicator for fetching operations */
  showOnFetch?: boolean;
  /** Show indicator for mutation operations */
  showOnMutation?: boolean;
}

/**
 * A thin progress bar at the top of the page that shows when
 * any API request is in progress.
 *
 * Uses TanStack Query's global state to automatically detect
 * ongoing operations without any manual tracking.
 */
export function GlobalLoadingIndicator({
  className,
  showOnFetch = true,
  showOnMutation = true,
}: GlobalLoadingIndicatorProps) {
  const isFetching = useIsFetching();
  const isMutating = useIsMutating();

  const isLoading =
    (showOnFetch && isFetching > 0) || (showOnMutation && isMutating > 0);

  if (!isLoading) return null;

  return (
    <div
      className={cn(
        'fixed top-0 left-0 right-0 z-50 h-1 overflow-hidden bg-primary/20',
        className
      )}
      role="progressbar"
      aria-label="Loading"
      aria-busy="true"
    >
      <div className="h-full w-full bg-primary animate-loading-bar" />
    </div>
  );
}

export default GlobalLoadingIndicator;
